import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatAmountCommaSeperator, scrollPanelFieldsToTop, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BasicSetupFormToolbar } from "../BasicSetupFormToolbar";
import { DDLDataTable } from "../DDLDataTable";
import { GeneralSettingsInfoSetupFieldsPanel } from "./Panels/GeneralSettingsInfoSetupFieldsPanel";
import { GeneralSettingsInfoSetupFieldsSecondPanel } from "./Panels/GeneralSettingsInfoSetupFieldsSecondPanel";
import { GeneralSettingsService } from "../../services/GeneralSettingsService";
import { GeneralSettingsSetupToolbar } from "./GeneralSettingsSetupToolbar";
import { GeneralSettingsSetupValidationSchema } from "./validator";
import { IAppState } from "../../store/reducers";
import { IGeneralSettings } from "../../models/GeneralSettings";
import { IGetListRequest } from "../../models";
import { setSelectedConditions } from "../../store/actions/conditionsActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IGeneralSettingsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "generalSettings-single-data-record";
// const GENERAL_SETTINGS_LIST_DATA_QUERY_KEY =
// 	"general-settings-list-data-record";

const useGetData = (
	clientId: string,
	// id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return GeneralSettingsService.getByClientId(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

// const useGeneralSettings = (
// 	clientId: string,
// 	params: IGetListRequest,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [GENERAL_SETTINGS_LIST_DATA_QUERY_KEY, params],
// 		queryFn: (data) => {
// 		
// 			return GeneralSettingsService.getGeneralSettings(
// 				clientId,
// 				params,
// 				navigateToLogout
// 			);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

export const GeneralSettingsSetup: React.FC<IGeneralSettingsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [generalSettingsId, setGeneralSettingsId] = useState<number>(0);
	const [base64Logo, setBase64Logo] = useState<string | null>(null);
	const [savedVacationPayOnPaidVacPay, setSavedVacationPayOnPaidVacPay] = useState<any>(null);

	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	// const [editRecordId, setEditRecordId] = useState(id);

	const { isPending: isSubmitting, mutate: submitGeneralSettingsFormFields } =
		useMutation({
			mutationFn: GeneralSettingsService.create,
			onSuccess: (res: IGeneralSettings | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIGeneralSettingsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/settings/general/setup/${clientId}`;
					navigate(path);


					// Notify other tabs
					localStorage.setItem('generalSettingsUpdated', Date.now().toString());

					return Promise.all([
						// queryClient.invalidateQueries({
						// 	queryKey: [GENERAL_SETTINGS_LIST_DATA_QUERY_KEY],
						// }),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIGeneralSettingsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/settings/general/setup/${clientId}`;
					navigate(path);

					// Notify other tabs
					localStorage.setItem('generalSettingsUpdated', Date.now().toString());

					return Promise.all([
						// queryClient.invalidateQueries({
						// 	queryKey: [GENERAL_SETTINGS_LIST_DATA_QUERY_KEY],
						// }),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		// editRecordId,
		navigateToLogout
	);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	// const { isPending, data } = useGeneralSettings(
	// 	clientId,
	// 	queryParams,
	// 	navigateToLogout
	// );

	// const generalSettingsList: any | null = data;

	// const upadtedGeneralSettingsListItems = useMemo(() => {
	// 	return (
	// 		generalSettingsList &&
	// 		generalSettingsList.items &&
	// 		generalSettingsList?.items.map((item: any) => ({
	// 			...item,
	// 			label: (
	// 				<Link
	// 					to={`/settings/general/setup/${item.id}`}
	// 					className="no-underline"
	// 					style={{ color: theme.palette.primary.light }}
	// 					onClick={() => setEditRecordId(item.id)}
	// 				>
	// 					{item.name}
	// 				</Link>
	// 			),
	// 		}))
	// 	);
	// }, [generalSettingsList]);

	const formMethods = useForm<any>({
		resolver: yupResolver(GeneralSettingsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onSubmit = (values: any) => {

		let payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			repeatColumnHeading: values.repeatColumnHeading,
			timeFormat: values.timeFormat,
			employeeId: values.employeeId,
			allEmployeesPaymentMethod: values.allEmployeesPaymentMethod,
			vacationPayonPaidVacPay: values.vacationPayonPaidVacPay,
			payrollRemittanceMethod: values.payrollRemittanceMethod,
			vacationPayOnPaidVacPay: values.vacationPayOnPaidVacPay,
			vacationPay: values.vacationPay,
			payoutVacationpay: values.payoutVacationpay,
			commission: values.commission,
			overtimeHours: values.overtimeHours,
			edrFields: values.edrFields,
			bonus: values.bonus,
			nonPeriodicPayment: values.nonPeriodicPayment,
			retroactivePayIncrease: values.retroactivePayIncrease,
			employeeSelectionInPayroll: values.employeeSelectionInPayroll,
			hourlyPayRate: values.hourlyPayRate,
			companyLogo: base64Logo,
			regularDailyHoursForEmployees: values.regularDailyHoursForEmployees,
		};
		submitGeneralSettingsFormFields(payload);
	};

	useEffect(() => {
		if (clientId && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "clientId") {
					let path = `/settings/general/setup/${clientId}`
					navigate(path)
				}
				if (fieldName === 'vacationPayOnPaidVacPay') {
					setSavedVacationPayOnPaidVacPay(fieldValue)
				}
			});
		}

		// const regularDailyHoursForEmployees = formMethods.getValues('regularDailyHoursForEmployees')
		// if (!regularDailyHoursForEmployees || regularDailyHoursForEmployees === 0) {
		// 	formMethods.setValue('regularDailyHoursForEmployees', formatAmountCommaSeperator(8))
		// }
	}, [clientId, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteGeneralSettingsById } =
		useMutation({
			mutationFn: GeneralSettingsService.delGeneralSettings,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the GeneralSettings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				setBase64Logo(null);
				scrollPanelFieldsToTop();
				let path = `/settings/general/setup`;
				navigate(path);
				return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [GENERAL_SETTINGS_LIST_DATA_QUERY_KEY],
					// }),
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					}),
				]);
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteGeneralSettingsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				generalSettingsId: generalSettingsId,
				navigateToLogout: navigateToLogout,
			};
			deleteGeneralSettingsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setGeneralSettingsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setGeneralSettingsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveGeneralSettingsById } =
		useMutation({
			mutationFn: GeneralSettingsService.archiveGeneralSettings,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the GeneralSettings fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/settings/general/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveGeneralSettingsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				generalSettingsId: generalSettingsId,
				navigateToLogout: navigateToLogout,
			};
			archiveGeneralSettingsById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setGeneralSettingsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: IGeneralSettings | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/settings/general/setup/${params.value}`;
		navigate(path);
	}, []);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		setBase64Logo(null);
		scrollPanelFieldsToTop();
		let path = `/settings/general/setup`;
		navigate(path);
	};

	const expandedLabels = ["Settings", "General"];
	const expandedGeneralSettingsSetupOrSetupItem = "General"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "General" &&
				item.label === expandedGeneralSettingsSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedGeneralSettingsSetupOrSetupItem && "/settings/general/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		generalSettingsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
		// sx={{ pb: "5rem", "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteGeneralSettingsField}
				cancelActionHandler={onCloseHandler}
				testId="generalSettings-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
							<TwoColumnLayout
								leftColumnContent={
									<StandardPanel
										title="GeneralSettings"
										testId={`generalSettings`}
										showTitle={false}
										borderHide={true}
									>
										<GeneralSettingsInfoSetupFieldsPanel
											formMethods={formMethods}
											savedVacationPayOnPaidVacPay={savedVacationPayOnPaidVacPay}
										/>
									</StandardPanel>
								}
								rightColumnContent={
									<StandardPanel
										title="GeneralSettings"
										testId={`generalSettings`}
										showTitle={false}
										borderHide={true}
									>
										<GeneralSettingsInfoSetupFieldsSecondPanel
											formMethods={formMethods}
											logo={(value: string | null) => setBase64Logo(value)}
										/>
									</StandardPanel>
								}
								toolbar={
									// <GeneralSettingsSetupToolbar
									// 	onResetClicked={onResetClicked}
									// 	onDeleteClicked={onDeleteClicked}
									// 	isSubmitting={false}
									// 	id={id}
									// />
									<BasicSetupFormToolbar
										// onResetClicked={onResetClicked}
										// onDeleteClicked={onDeleteClicked}
										isSubmitting={isSubmitting}
										// id={id}
										pageTitle={"General Settings"}
									/>
								}
								testId="form-layout"
							// showFullDiaplay={true}
							// toolbarPosition="bottom"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						{/* <Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									generalSettingsList && upadtedGeneralSettingsListItems
										? upadtedGeneralSettingsListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									generalSettingsList && generalSettingsList.total
										? generalSettingsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
							/>
						</Grid> */}
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			{/* <EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveGeneralSettingsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/> */}

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"generalSettings-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
